// MoreSetting.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';

import NeedAuth from '../container/NeedAuth.js';

import DropdownIcon from '../../img/ic_dropdown_w.svg';
import BackIcon from '../../img/ic_topbar_back.svg';

import { NULL_FUNCTION } from '../resource/defaults.js';
import { getIsChinaPartner } from '../resource/partner.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

const isChinaPartner = getIsChinaPartner();

const HORIZONTAL_PADDING = 16;

export const MoreSetting = ({
  shouldShowAboutSwag = false,
  sendDataToSentry = NULL_FUNCTION,
  close = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  return (
    <StyledMoreSetting>
      <Header>
        <CloseButton role="button" onClick={close}>
          <CloseIcon src={BackIcon} title="close" alt="close" />
        </CloseButton>
        <HeaderTitle>
          {t('more', {
            ns: TranslationNamespace.GENERAL,
          })}
        </HeaderTitle>
      </Header>
      <Body>
        <Section>
          <Lists>
            <ItemWrapper>
              <InternalLink to="/tos">
                {t('terms_of_service', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </InternalLink>
            </ItemWrapper>
            <ItemWrapper>
              <InternalLink to="/privacy">
                {t('privacy_policy', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </InternalLink>
            </ItemWrapper>
            <ItemWrapper>
              <InternalLink to="/acceptable-use-policy">
                {t('acceptable_use_policy', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </InternalLink>
            </ItemWrapper>
            <ItemWrapper>
              <InternalLink to="/anti-slavery">
                {t('anti_slavery', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </InternalLink>
            </ItemWrapper>
            <ItemWrapper>
              <InternalLink to="/complaint-policy">
                {t('complaint_policy', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </InternalLink>
            </ItemWrapper>
            <ItemWrapper>
              <InternalLink to="/cookie-policy">
                {t('cookie_policy', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </InternalLink>
            </ItemWrapper>
            <ItemWrapper>
              <InternalLink to="/settings/more/debug">
                Debug
                <OpenModalIcon src={DropdownIcon} aria-hidden />
              </InternalLink>
            </ItemWrapper>
            <ItemWrapper>
              <InternalLink to="/settings/more/drm">
                DRM
                <OpenModalIcon src={DropdownIcon} aria-hidden />
              </InternalLink>
            </ItemWrapper>
            {shouldShowAboutSwag && (
              <ItemWrapper>
                <InternalLink
                  to="/settings/more/about"
                  onClick={sendDataToSentry}
                >
                  {t('about_swag', {
                    ns: TranslationNamespace.SETTINGS,
                  })}
                  <OpenModalIcon src={DropdownIcon} aria-hidden />
                </InternalLink>
              </ItemWrapper>
            )}
          </Lists>
        </Section>
        <Section>
          <Lists>
            <ItemWrapper>
              <InternalLink to={'/settings/2257'}>
                {t('2257_title', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </InternalLink>
            </ItemWrapper>
          </Lists>
        </Section>
        {!isChinaPartner && (
          <NeedAuth>
            <Section data-key="authed">
              <Lists>
                <ItemWrapper>
                  <DeleteAccountItem to="/settings/more/delete-account">
                    {t('delete_account', {
                      ns: TranslationNamespace.SETTINGS,
                    })}
                    <OpenModalIcon src={DropdownIcon} aria-hidden />
                  </DeleteAccountItem>
                </ItemWrapper>
              </Lists>
            </Section>
          </NeedAuth>
        )}
      </Body>
    </StyledMoreSetting>
  );
};

MoreSetting.propTypes = {
  shouldShowAboutSwag: PropTypes.bool,
  sendDataToSentry: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
};

const StyledMoreSetting = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background-color: #191919;
  padding-top: var(--space-to-pinned-notification);
`;

const Header = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-bottom: 1px solid rgb(100, 100, 100, 0.5);
  height: 58px;
  background-color: #232323;
`;

const CloseButton = styled.div`
  box-sizing: content-box;
  position: absolute;
  top: 0;
  left: 0;
  padding: 14px ${HORIZONTAL_PADDING}px;
  cursor: pointer;
  height: 30px;
  :hover {
    opacity: 0.7;
  }
`;

const CloseIcon = styled.img`
  height: 100%;
  width: auto;
  vertical-align: top;
`;

const HeaderTitle = styled.div`
  margin: 0px;
  font-size: 16px;
  color: #ffffff;
`;

const Body = styled.div`
  overflow-y: auto;
`;

const Section = styled.div`
  margin-top: 32px;
`;

const Lists = styled.div`
  margin-bottom: 30px;
  border-top: 1px solid rgba(100, 100, 100, 0.5);
  border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  background-color: #232323;
`;

const ItemWrapper = styled.div`
  position: relative;
  margin-left: ${HORIZONTAL_PADDING}px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.25);
  color: #ffffff;
  font-size: 16px;
  &:last-child {
    border-bottom: 0;
  }
  cursor: ${({ noPointer }) => (noPointer ? 'default' : 'pointer')};
`;

const InternalLink = styled(Link)`
  display: block;
  padding-top: 14px;
  padding-bottom: 14px;
  width: 100%;
  height: 100%;
`;

const OpenModalIcon = styled.img`
  position: absolute;
  top: 50%;
  right: ${HORIZONTAL_PADDING}px;
  height: 15px;
  transform: translateY(-50%) rotate(-90deg);
`;

const DeleteAccountItem = styled(Link)`
  flex: none;
  display: block;
  padding: 16px 0;
  margin-right: 48px;
  font-size: 16px;
`;
export default MoreSetting;
